import React, { useCallback, useEffect, useState } from 'react';
import styled from 'astroturf/react';
import routes from 'js/core_pre_webpack/routes';
import { Body } from 'js/design_system/Body';
import { DeprecatedBreakpoint } from 'js/design_system/DeprecatedBreakpoint';
import { CurrentUser } from 'js/core_post_webpack/models/user';
import AvatarBase from 'js/social/shared/Avatar';
import AccountMultipleOutline from 'js/svg/colorless/account_multiple_outline.svg';
import BellOutlineIcon from 'js/svg/colorless/bell_outline.svg';
import LibraryIcon from 'js/svg/colorless/library.svg';
import MessageOutlineIcon from 'js/svg/colorless/message_outline.svg';
import PersonOutlineIcon from 'js/svg/colorless/person_outline.svg';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import { getSocialImpressionSource } from 'js/social/ImpressionHelper';
import { getSuggestionGroups } from 'js/social/api/suggested_api';
import { Button } from 'js/social/shared/design_system/Button';
import { LinkButton } from 'js/social/shared/design_system/LinkButton';
import useBreakpoint from 'js/utils/hooks/useDeprecatedBreakpoint';

type Props = {
  onPost: () => unknown;
  user: CurrentUser;
  followersCount: number;
  followeesCount: number;
  suggestions?: {
    count: number;
    hasMore: boolean;
  };
};

type AffiliationProps = {
  department?: string;
  university?: string;
};

type ButtonAction = {
  icon: typeof BellOutlineIcon;
  onClick?: () => unknown;
  text: string;
  url?: string;
};

const Container = styled.div`
  color: $ds-black;
`;

const Avatar = styled(AvatarBase)`
  --size: 88px;
  display: block;
  margin-bottom: 8px;
`;

const DisplayName = styled(Body)`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AffiliationBody = styled(Body)`
  color: $ds-gray-dark;
`;

const FollowingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 2px 0 8px 20px;

  @include for-tablet-up {
    margin-left: 40px;
  }
`;

const FollowingRow = styled.div`
  display: flex;

  &:hover a {
    text-decoration-line: underline;
  }

  > :first-child {
    flex-grow: 1;
  }
`;

const Buttons = styled(ImpressionWatcher)`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PostButton = styled(Button)`
  width: 100%;
  margin-top: 24px;
`;

const Affiliation: React.FC<AffiliationProps> = ({
  department,
  university,
}) => {
  if (university && department) {
    return (
      <AffiliationBody size="xs">
        {department} at {university}
      </AffiliationBody>
    );
  }

  if (!university && !department) return null;

  return (
    <AffiliationBody size="xs">{university ?? department}</AffiliationBody>
  );
};

const IMPRESSION_PAGE = 'home';
const IMPRESSION_SECTION = 'sidebar';

const ActionNav: React.FC<Props> = ({
  onPost,
  user,
  followeesCount,
  followersCount,
  suggestions,
}) => {
  const [suggestionsCount, setSuggestionsCount] = useState(suggestions?.count);
  const [hasMoreSuggestions, setHasMoreSuggestions] = useState(
    suggestions?.hasMore,
  );
  const condensed = useBreakpoint() < DeprecatedBreakpoint.tablet;

  useEffect(() => {
    if (suggestions === undefined) {
      getSuggestionGroups(6).then(({ count, has_more }) => {
        setSuggestionsCount(count);
        setHasMoreSuggestions(has_more);
      });
    }
  }, [suggestions]);

  const responsiveCount = useCallback(
    (count: number) => {
      if (condensed) {
        const orders = ['', 'k', 'm'];
        let orderIdx = 0;
        while (orderIdx < orders.length && count >= 1000) {
          count /= 1000;
          orderIdx++;
        }
        return `${Math.round(count).toLocaleString()}${orders[orderIdx]}`;
      } else {
        return count.toLocaleString();
      }
    },
    [condensed],
  );

  const networkTabLink = useCallback(
    (tab: string) => {
      return routes.network_new_profile_url({
        page_name: user.page_name,
        subdomain: user.domain_name,
        tab,
      });
    },
    [user],
  );

  const ACTIONS: ButtonAction[] = [
    {
      icon: MessageOutlineIcon,
      text: 'Messages',
      url: '/messages',
    },
    {
      icon: BellOutlineIcon,
      text: 'Notifications',
      url: '/notifications',
    },
    {
      icon: LibraryIcon,
      text: 'Bookmarks',
      url: routes.bookmarks_url({
        trigger: 'social_actions',
      }),
    },
    {
      icon: PersonOutlineIcon,
      text: 'Profile',
      url: user.url,
    },
  ];

  return (
    <Container>
      <ImpressionWatcher
        source={getSocialImpressionSource(
          IMPRESSION_PAGE,
          IMPRESSION_SECTION,
          'prof',
        )}
        entity_type={ImpressionEntity.User}
        entity_id={user.id}
      >
        <Avatar
          fullName={user.display_name as string}
          photoUrl={user.photo}
          profileUrl={user.url}
        />
      </ImpressionWatcher>
      <DisplayName bold>{user.display_name}</DisplayName>
      <Affiliation
        department={user.department?.name}
        university={user.department?.university?.name}
      />

      <ImpressionWatcher
        source={getSocialImpressionSource(
          IMPRESSION_PAGE,
          IMPRESSION_SECTION,
          'network',
        )}
      >
        <a href={networkTabLink('suggested')}>
          <Button
            variant="text"
            size="sm"
            icon={<AccountMultipleOutline size={20} />}
            style={{ marginTop: 24 }}
          >
            My&nbsp;Network
          </Button>
        </a>
      </ImpressionWatcher>
      <FollowingContainer>
        <ImpressionWatcher
          source={getSocialImpressionSource(
            IMPRESSION_PAGE,
            IMPRESSION_SECTION,
            'network_flrs',
          )}
        >
          <FollowingRow>
            <LinkButton
              href={networkTabLink('followers')}
              color="gray"
              weight="regular"
            >
              Followers
            </LinkButton>
            <LinkButton href={networkTabLink('followers')}>
              {responsiveCount(followersCount)}
            </LinkButton>
          </FollowingRow>
        </ImpressionWatcher>
        <ImpressionWatcher
          source={getSocialImpressionSource(
            IMPRESSION_PAGE,
            IMPRESSION_SECTION,
            'network_flng',
          )}
        >
          <FollowingRow>
            <LinkButton
              href={networkTabLink('following')}
              color="gray"
              weight="regular"
            >
              Following
            </LinkButton>
            <LinkButton href={networkTabLink('following')}>
              {responsiveCount(followeesCount)}
            </LinkButton>
          </FollowingRow>
        </ImpressionWatcher>
        <ImpressionWatcher
          source={getSocialImpressionSource(
            IMPRESSION_PAGE,
            IMPRESSION_SECTION,
            'network_sugg',
          )}
        >
          <FollowingRow>
            <LinkButton
              href={networkTabLink('suggested')}
              color="gray"
              weight="regular"
            >
              Suggested
            </LinkButton>
            {suggestionsCount !== undefined &&
              hasMoreSuggestions !== undefined && (
                <LinkButton href={networkTabLink('suggested')}>
                  {responsiveCount(suggestionsCount)}
                  {hasMoreSuggestions ? '+' : ''}
                </LinkButton>
              )}
          </FollowingRow>
        </ImpressionWatcher>
      </FollowingContainer>

      <Buttons
        source={getSocialImpressionSource(
          IMPRESSION_PAGE,
          IMPRESSION_SECTION,
          'links',
        )}
      >
        {ACTIONS.map((action) => (
          <a key={action.text} href={action.url}>
            <Button variant="text" size="sm" icon={<action.icon size={20} />}>
              {action.text}
            </Button>
          </a>
        ))}
      </Buttons>

      <ImpressionWatcher
        source={getSocialImpressionSource(
          IMPRESSION_PAGE,
          IMPRESSION_SECTION,
          'post',
        )}
      >
        <PostButton size="lg" variant="secondary" onClick={onPost}>
          Create post
        </PostButton>
      </ImpressionWatcher>
    </Container>
  );
};

export default ActionNav;
